import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
  type ChartDataset,
  type Plugin,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { GRAPH_COLORS, GRAPH_CURVE } from '../../config/designTokens';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
);

const MAX_DISPLAY_POINTS = 500;
const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
                   'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function downsample<T>(arr: T[], max: number): T[] {
  if (arr.length <= max) return arr;
  const step = Math.ceil(arr.length / max);
  return arr.filter((_, i) => i % step === 0);
}

function getMidnightsInRange(minMs: number, maxMs: number): number[] {
  const result: number[] = [];
  const d = new Date(minMs);
  d.setHours(24, 0, 0, 0);
  while (d.getTime() <= maxMs) {
    result.push(d.getTime());
    d.setDate(d.getDate() + 1);
  }
  return result;
}

function getMonthStartsInRange(minMs: number, maxMs: number): number[] {
  const result: number[] = [];
  const d = new Date(minMs);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  if (d.getTime() <= minMs) d.setMonth(d.getMonth() + 1);
  while (d.getTime() <= maxMs) {
    result.push(d.getTime());
    d.setMonth(d.getMonth() + 1);
  }
  return result;
}

/** X tick config matching Hub history.js _getXTickConfig */
function getXTickConfig(visibleMs: number) {
  const visibleH = visibleMs / 3600000;
  if (visibleH <= 12)  return { minorMs: 30 * 60000,    majorMs: 3600000 };
  if (visibleH <= 24)  return { minorMs: 3600000,        majorMs: 3 * 3600000 };
  if (visibleH <= 48)  return { minorMs: 2 * 3600000,    majorMs: 6 * 3600000 };
  if (visibleH <= 168) return { minorMs: 6 * 3600000,    majorMs: 12 * 3600000 };
  if (visibleH <= 744) return { minorMs: 86400000,        majorMs: 7 * 86400000 };
  return               { minorMs: 7 * 86400000,           majorMs: 30 * 86400000 };
}

/** Y axis config — min/max with 15% padding + nice step, mirrors Hub _getYAxisConfig */
function getYAxisConfig(temps: number[], thresholds: { alarmHigh?: number | null; alarmLow?: number | null }) {
  if (temps.length === 0) return {};
  let tMin = Math.min(...temps);
  let tMax = Math.max(...temps);
  if (thresholds.alarmHigh != null) tMax = Math.max(tMax, thresholds.alarmHigh);
  if (thresholds.alarmLow  != null) tMin = Math.min(tMin, thresholds.alarmLow);
  const range = tMax - tMin || 2;
  const pad = range * 0.15;
  const yMin = tMin - pad;
  const yMax = tMax + pad;
  // Nice step: pick from [0.5, 1, 2, 5, 10] so we get ~6-8 ticks
  const rawStep = (yMax - yMin) / 7;
  const candidates = [0.5, 1, 2, 5, 10, 20, 50];
  const step = candidates.find(s => s >= rawStep) ?? 50;
  return {
    min: yMin,
    max: yMax,
    // _stepSize is a custom option read by afterBuildTicks to snap ticks to multiples (mirrors Hub)
    _stepSize: step,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    afterBuildTicks(scale: any) {
      const s = (scale.options._stepSize as number | undefined) || 1;
      const first = Math.ceil(scale.min / s) * s;
      const last  = Math.floor(scale.max / s) * s;
      const ticks: { value: number }[] = [];
      for (let v = first; v <= last + 1e-9; v = Math.round((v + s) * 1e9) / 1e9) {
        ticks.push({ value: v });
      }
      scale.ticks = ticks;
    },
    ticks: { stepSize: step, color: GRAPH_COLORS.axis, callback: (v: number | string) => Number(v).toFixed(1) },
  };
}

/** Vertical crosshair plugin */
const crosshairPlugin: Plugin = {
  id: 'crosshair',
  afterDraw(chart) {
    const { ctx, chartArea, tooltip } = chart as Chart & { tooltip: { _active?: { element: { x: number } }[] } };
    if (!tooltip?._active?.length) return;
    const x = tooltip._active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(107, 114, 128, 0.5)';
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.restore();
  },
};

/** xSuperTicks plugin — draws double-row axis labels + local-time grid lines, mirrors Hub history.js */
const xSuperTicksPlugin: Plugin = {
  id: 'xSuperTicks',
  afterDraw(chart) {
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];
    if (!xScale || !yScale) return;

    const minMs = xScale.min;
    const maxMs = xScale.max;
    const visibleMs = maxMs - minMs;
    const visibleH = visibleMs / 3600000;
    const { minorMs, majorMs } = getXTickConfig(visibleMs);
    const axisY = yScale.bottom;
    const ctx = chart.ctx;

    // Build major ticks
    const majorTicks: { mts: number; label: string }[] = [];
    if (visibleH <= 168) {
      const d0 = new Date(minMs);
      d0.setHours(0, 0, 0, 0);
      let t = d0.getTime();
      while (t <= maxMs) {
        if (t >= minMs) {
          const d = new Date(t);
          majorTicks.push({ mts: t, label: `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}` });
        }
        t += majorMs;
      }
    } else {
      const d0 = new Date(minMs);
      d0.setHours(0, 0, 0, 0);
      const dow = d0.getDay();
      d0.setDate(d0.getDate() + (dow === 1 ? 0 : (8 - dow) % 7));
      let t = d0.getTime();
      while (t <= maxMs) {
        if (t >= minMs) {
          const d = new Date(t);
          majorTicks.push({ mts: t, label: `${d.getDate()} ${MONTHS_FR[d.getMonth()]}` });
        }
        t += 7 * 86400000;
      }
    }

    // Build super ticks (midnight for ≤7j, 1st of month for >7j)
    const superTicks: { mts: number; label: string }[] = [];
    if (visibleH <= 168) {
      getMidnightsInRange(minMs, maxMs).forEach(mts => {
        const d = new Date(mts);
        superTicks.push({ mts, label: `${d.getDate()} ${MONTHS_FR[d.getMonth()]}` });
      });
    } else {
      getMonthStartsInRange(minMs, maxMs).forEach(mts => {
        const d = new Date(mts);
        superTicks.push({ mts, label: MONTHS_FR[d.getMonth()] });
      });
    }

    // Build minor ticks
    const minorTicks: number[] = [];
    const d0 = new Date(minMs);
    d0.setHours(0, 0, 0, 0);
    let t = d0.getTime();
    while (t <= maxMs) {
      if (t >= minMs) minorTicks.push(t);
      t += minorMs;
    }

    ctx.save();
    ctx.setLineDash([]);

    const majorSet = new Set(majorTicks.map(m => m.mts));
    const superSet = new Set(superTicks.map(s => s.mts));

    // Grid lines
    ctx.lineWidth = 1;
    for (const mts of minorTicks) {
      const px = xScale.getPixelForValue(mts);
      if (px < xScale.left || px > xScale.right) continue;
      ctx.strokeStyle = (majorSet.has(mts) || superSet.has(mts)) ? '#e0e0e0' : '#efefef';
      ctx.beginPath();
      ctx.moveTo(px, yScale.top);
      ctx.lineTo(px, axisY);
      ctx.stroke();
    }

    // Minor tick marks
    ctx.strokeStyle = '#9ca3af';
    for (const mts of minorTicks) {
      if (majorSet.has(mts) || superSet.has(mts)) continue;
      const px = xScale.getPixelForValue(mts);
      if (px < xScale.left || px > xScale.right) continue;
      ctx.beginPath();
      ctx.moveTo(px, axisY);
      ctx.lineTo(px, axisY + 4);
      ctx.stroke();
    }

    // Major tick marks + row-1 labels
    ctx.strokeStyle = '#6b7280';
    ctx.fillStyle = '#6b7280';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'center';
    for (const { mts, label } of majorTicks) {
      const px = xScale.getPixelForValue(mts);
      if (px < xScale.left || px > xScale.right) continue;
      ctx.beginPath();
      ctx.moveTo(px, axisY);
      ctx.lineTo(px, axisY + 6);
      ctx.stroke();
      ctx.fillText(label, px, axisY + 18);
    }

    // Super tick lines + row-2 labels
    ctx.strokeStyle = 'rgba(107, 114, 128, 0.6)';
    ctx.lineWidth = 1.5;
    for (const { mts, label } of superTicks) {
      const px = xScale.getPixelForValue(mts);
      if (px < xScale.left || px > xScale.right) continue;
      ctx.beginPath();
      ctx.moveTo(px, yScale.top);
      ctx.lineTo(px, axisY + 6);
      ctx.stroke();
      ctx.fillStyle = '#374151';
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(label, px, axisY + 32);
    }

    ctx.restore();
  },
};

export interface ProbeChartProps {
  officialData: { timestamp: string; value: number }[];
  advData: { timestamp: string; value: number }[];
  unit: string;
  thresholds: {
    alarmLow?: number | null;
    alarmHigh?: number | null;
    warningLow?: number | null;
    warningHigh?: number | null;
  };
  hideAdv: boolean;
  periodStart: string; // ISO — fixes x-axis min
  periodEnd: string;   // ISO — fixes x-axis max
}

export default function ProbeChart({
  officialData,
  advData,
  unit,
  thresholds,
  hideAdv,
  periodStart,
  periodEnd,
}: ProbeChartProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const [showControls, setShowControls] = useState(false);

  const xMin = new Date(periodStart).getTime();
  const xMax = new Date(periodEnd).getTime();

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const sampledOfficial = downsample(officialData, MAX_DISPLAY_POINTS);
    const sampledAdv = hideAdv ? [] : downsample(advData, MAX_DISPLAY_POINTS);

    const officialPoints = sampledOfficial.map((m) => ({ x: new Date(m.timestamp).getTime(), y: m.value }));
    const advPoints = sampledAdv.map((m) => ({ x: new Date(m.timestamp).getTime(), y: m.value }));

    // Threshold lines span the full period
    const thresholdDatasets: ChartDataset<'line'>[] = [];
    if (thresholds.alarmHigh != null) {
      thresholdDatasets.push({
        label: t('chart.alarmHigh'),
        data: [{ x: xMin, y: thresholds.alarmHigh }, { x: xMax, y: thresholds.alarmHigh }],
        borderColor: GRAPH_COLORS.alarmThreshold,
        borderWidth: GRAPH_CURVE.thresholdWidth,
        borderDash: GRAPH_CURVE.thresholdDash,
        pointRadius: 0,
        fill: false,
        tension: 0,
        order: 10,
      });
    }
    if (thresholds.alarmLow != null) {
      thresholdDatasets.push({
        label: t('chart.alarmLow'),
        data: [{ x: xMin, y: thresholds.alarmLow }, { x: xMax, y: thresholds.alarmLow }],
        borderColor: GRAPH_COLORS.alarmThreshold,
        borderWidth: GRAPH_CURVE.thresholdWidth,
        borderDash: GRAPH_CURVE.thresholdDash,
        pointRadius: 0,
        fill: false,
        tension: 0,
        order: 10,
      });
    }

    const datasets: ChartDataset<'line'>[] = [
      {
        label: t('chart.official'),
        data: officialPoints,
        borderColor: GRAPH_COLORS.curveOfficial,
        borderWidth: 2,
        pointRadius: sampledOfficial.length > 100 ? 0 : 2,
        fill: false,
        tension: 0.1,
        spanGaps: false,
        order: 1,
      },
      ...(advPoints.length > 0
        ? [{
            label: t('chart.adv'),
            data: advPoints,
            borderColor: GRAPH_COLORS.curveAdv,
            borderWidth: GRAPH_CURVE.advWidth,
            borderDash: GRAPH_CURVE.advDash,
            pointRadius: 0,
            fill: false,
            tension: 0.1,
            spanGaps: false,
            order: 2,
          } as ChartDataset<'line'>]
        : []),
      ...thresholdDatasets,
    ];

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      layout: { padding: { bottom: 0 } },
      scales: {
        x: {
          type: 'time',
          min: xMin,
          max: xMax,
          // Ticks hidden — xSuperTicksPlugin draws them in local time
          ticks: { display: false },
          grid: { display: false },
          // Extra bottom padding for double-row labels
          afterFit(scale) { scale.height += 36; },
          time: {
            unit: 'hour',
            tooltipFormat: 'dd/MM/yyyy HH:mm',
            displayFormats: { hour: 'HH:mm', day: 'dd MMM' },
          },
        },
        y: {
          ...getYAxisConfig([...officialPoints, ...advPoints].map(p => p.y), thresholds),
          grid: { color: GRAPH_COLORS.gridMajor },
          title: { display: true, text: unit, color: GRAPH_COLORS.axis },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'line',
            padding: 20,
            color: '#374151',
            generateLabels(_chart) {
              const items = [];
              items.push({ text: t('chart.official'), fillStyle: 'transparent', strokeStyle: GRAPH_COLORS.curveOfficial, lineWidth: 2, lineDash: [], hidden: false, pointStyle: 'line' as const });
              if (advPoints.length > 0)
                items.push({ text: t('chart.adv'), fillStyle: 'transparent', strokeStyle: GRAPH_COLORS.curveAdv, lineWidth: GRAPH_CURVE.advWidth, lineDash: [...GRAPH_CURVE.advDash], hidden: false, pointStyle: 'line' as const });
              if (thresholds.alarmHigh != null)
                items.push({ text: t('chart.alarmHigh'), fillStyle: 'transparent', strokeStyle: GRAPH_COLORS.alarmThreshold, lineWidth: GRAPH_CURVE.thresholdWidth, lineDash: [...GRAPH_CURVE.thresholdDash], hidden: false, pointStyle: 'line' as const });
              if (thresholds.alarmLow != null)
                items.push({ text: t('chart.alarmLow'), fillStyle: 'transparent', strokeStyle: GRAPH_COLORS.alarmThreshold, lineWidth: GRAPH_CURVE.thresholdWidth, lineDash: [...GRAPH_CURVE.thresholdDash], hidden: false, pointStyle: 'line' as const });
              return items;
            },
          },
        },
        tooltip: {
          callbacks: {
            title: (items) => items.length ? new Date(items[0].parsed.x as number).toLocaleString() : '',
            label: (ctx) => {
              if (ctx.dataset.label?.includes('larme')) return null as unknown as string;
              return `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(1)}${unit}`;
            },
          },
        },
        zoom: {
          pan: { enabled: true, mode: 'xy' },
          zoom: {
            wheel: { enabled: false },
            drag: { enabled: false },
            mode: 'xy',
          },
        },
      },
    };

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: { datasets },
      options,
      plugins: [crosshairPlugin, xSuperTicksPlugin],
    });

    // Scroll = pan X (identical to Hub history.js wheel handler)
    const canvas = canvasRef.current;
    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      chartRef.current?.pan({ x: -e.deltaY * 1.5 }, undefined, 'default');
    };
    canvas.addEventListener('wheel', wheelHandler, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', wheelHandler);
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officialData, advData, hideAdv, unit, thresholds, xMin, xMax]);

  const isEmpty = officialData.length === 0 && (hideAdv || advData.length === 0);

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-400 text-sm">{t('chart.noData')}</p>
      </div>
    );
  }

  const zoomAxis = (axis: 'x' | 'y', factor: number) => {
    const chart = chartRef.current;
    if (!chart) return;
    const scale = chart.scales[axis];
    if (!scale) return;
    const mid = (scale.max + scale.min) / 2;
    const half = (scale.max - scale.min) / 2 * factor;
    (chart.options.scales![axis] as { min: number; max: number }).min = mid - half;
    (chart.options.scales![axis] as { min: number; max: number }).max = mid + half;
    chart.update('none');
  };

  return (
    <div
      className="relative w-full"
      style={{ height: 360 }}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <canvas ref={canvasRef} />
      {/* Zoom controls — semi-transparent, visible on hover only */}
      <div
        className="absolute top-2 right-2 flex flex-col gap-0.5 transition-opacity duration-150"
        style={{ opacity: showControls ? 1 : 0, pointerEvents: showControls ? 'auto' : 'none' }}
      >
        <div className="flex gap-0.5">
          <button onClick={() => zoomAxis('y', 0.85)} className="zoom-btn !bg-white/70 hover:!bg-white/95" title="Zoom in Y">+</button>
          <div className="w-5" />
          <span className="zoom-btn !bg-white/70 cursor-default select-none">🔍</span>
        </div>
        <div className="flex gap-0.5">
          <button onClick={() => zoomAxis('y', 1.15)} className="zoom-btn !bg-white/70 hover:!bg-white/95" title="Zoom out Y">−</button>
          <button onClick={() => chartRef.current?.resetZoom()} className="zoom-btn !bg-white/70 hover:!bg-white/95" title={t('chart.resetZoom')}>↩</button>
          <div className="w-5" />
        </div>
        <div className="flex gap-0.5">
          <div className="w-5" />
          <button onClick={() => zoomAxis('x', 1.15)} className="zoom-btn !bg-white/70 hover:!bg-white/95" title="Zoom out X">−</button>
          <button onClick={() => zoomAxis('x', 0.85)} className="zoom-btn !bg-white/70 hover:!bg-white/95" title="Zoom in X">+</button>
        </div>
      </div>
    </div>
  );
}
